import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Printer, Trash2, Sun, Moon } from "lucide-react";
import { SmsButton } from "@/components/SmsButton";

const today = () => new Date().toISOString().slice(0, 10);
const money = (n) => `₹${Number(n || 0).toFixed(2)}`;

const EMPTY = { date: today(), shift: "morning", farmer_id: "", milk_type: "cow", quantity: "", fat: "", snf: "", clr: "", remarks: "" };

export default function Collections() {
  const { user, tenant } = useAuth();
  const { t } = useI18n();
  const qc = useQueryClient();
  const canAdd = ["tenant_owner", "operator"].includes(user.role);
  const canDelete = user.role === "tenant_owner";
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", shift: "all", milk_type: "all" });
  const [form, setForm] = useState(EMPTY);
  const [calc, setCalc] = useState(null);
  const [receipt, setReceipt] = useState(null);

  const { data: farmers } = useQuery({ queryKey: ["farmers-all"], queryFn: async () => (await api.get("/farmers/all")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["collections", page, filters],
    queryFn: async () => (await api.get("/collections", { params: {
      page, page_size: 10, date_from: filters.date_from, date_to: filters.date_to,
      shift: filters.shift === "all" ? "" : filters.shift, milk_type: filters.milk_type === "all" ? "" : filters.milk_type,
    } })).data,
  });

  // auto-calc rate/amount as user types
  useEffect(() => {
    const { milk_type, date, fat, snf, quantity } = form;
    if (fat && snf && quantity && Number(quantity) > 0) {
      const h = setTimeout(async () => {
        try {
          const { data } = await api.post("/collections/calculate", {
            milk_type, date, fat: Number(fat), snf: Number(snf), quantity: Number(quantity),
          });
          setCalc(data);
        } catch (e) { setCalc(null); }
      }, 350);
      return () => clearTimeout(h);
    }
    setCalc(null);
  }, [form.milk_type, form.date, form.fat, form.snf, form.quantity]);

  const create = useMutation({
    mutationFn: async () => (await api.post("/collections", {
      ...form, quantity: Number(form.quantity), fat: Number(form.fat), snf: Number(form.snf), clr: Number(form.clr || 0),
    })).data,
    onSuccess: (res) => {
      toast.success(`Collection ${res.collection_number} saved`);
      setReceipt(res);
      setForm((f) => ({ ...EMPTY, date: f.date, shift: f.shift, milk_type: f.milk_type }));
      setCalc(null);
      qc.invalidateQueries({ queryKey: ["collections"] });
      qc.invalidateQueries({ queryKey: ["dash-stats"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/collections/${id}`)).data,
    onSuccess: () => { toast.success("Collection deleted"); qc.invalidateQueries({ queryKey: ["collections"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const canSms = user.role === "tenant_owner";
  const mobileOf = (fid) => (farmers || []).find((f) => f.id === fid)?.mobile || "";
  const shiftMr = { morning: "सकाळ", evening: "संध्याकाळ" };
  const typeMr = { cow: "गाय", buffalo: "म्हैस" };
  const collectionSms = (c) => [
    tenant?.center_name,
    `पावती: ${c.collection_number}`,
    `दिनांक: ${c.date} ${shiftMr[c.shift] || c.shift}`,
    `शेतकरी: ${c.farmer_code} ${c.farmer_name}`,
    `प्रकार: ${typeMr[c.milk_type] || c.milk_type}`,
    `प्रमाण: ${c.quantity} L, FAT: ${c.fat}, SNF: ${c.snf}`,
    `दर: ₹${Number(c.rate || 0).toFixed(2)}/L`,
    `एकूण: ₹${Number(c.amount || 0).toFixed(2)}`,
    "धन्यवाद!",
  ].filter(Boolean).join("\n");

  return (
    <div className="space-y-6" data-testid="collections-page">
      <PageHeader title={t("col.title")} subtitle={t("col.subtitle")} breadcrumb={[t("crumb.center"), t("col.title")]} />

      <div className="grid gap-6 lg:grid-cols-3">
        {canAdd && (
          <Card className="lg:col-span-1" data-testid="collection-entry-form">
            <CardHeader><CardTitle className="font-heading text-lg">{t("col.newEntry")}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={set("date")} data-testid="collection-date-input" /></div>
                <div className="space-y-1.5">
                  <Label>Shift</Label>
                  <Select value={form.shift} onValueChange={set("shift")}>
                    <SelectTrigger data-testid="collection-shift-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="morning">Morning</SelectItem><SelectItem value="evening">Evening</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Farmer</Label>
                <Select value={form.farmer_id} onValueChange={set("farmer_id")}>
                  <SelectTrigger data-testid="collection-farmer-select"><SelectValue placeholder="Select farmer" /></SelectTrigger>
                  <SelectContent>{(farmers || []).map((f) => <SelectItem key={f.id} value={f.id}>{f.farmer_code} · {f.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Milk Type</Label>
                <Select value={form.milk_type} onValueChange={set("milk_type")}>
                  <SelectTrigger data-testid="collection-milktype-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cow">Cow</SelectItem><SelectItem value="buffalo">Buffalo</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Quantity (L)</Label><Input type="number" step="0.1" value={form.quantity} onChange={set("quantity")} data-testid="collection-quantity-input" /></div>
                <div className="space-y-1.5"><Label>FAT %</Label><Input type="number" step="0.1" value={form.fat} onChange={set("fat")} data-testid="collection-fat-input" /></div>
                <div className="space-y-1.5"><Label>SNF %</Label><Input type="number" step="0.1" value={form.snf} onChange={set("snf")} data-testid="collection-snf-input" /></div>
                <div className="space-y-1.5"><Label>CLR</Label><Input type="number" step="0.1" value={form.clr} onChange={set("clr")} data-testid="collection-clr-input" /></div>
              </div>
              <div className="rounded-lg border bg-muted/40 p-3">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Rate / L</span><span className="font-semibold tnum" data-testid="calc-rate">{calc ? money(calc.rate) : "—"}</span></div>
                <div className="mt-1 flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-heading text-lg font-bold text-primary tnum" data-testid="calc-amount">{calc ? money(calc.amount) : "—"}</span></div>
              </div>
              <Button className="w-full" onClick={() => create.mutate()} disabled={!form.farmer_id || !calc || create.isPending} data-testid="submit-collection-btn">
                {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save & Print Receipt
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className={canAdd ? "lg:col-span-2" : "lg:col-span-3"}>
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="font-heading text-lg">{t("col.history")}</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Input type="date" className="w-auto" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="filter-date-from" />
              <Input type="date" className="w-auto" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="filter-date-to" />
              <Select value={filters.shift} onValueChange={(v) => setFilters((f) => ({ ...f, shift: v }))}>
                <SelectTrigger className="w-28" data-testid="filter-shift"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All shifts</SelectItem><SelectItem value="morning">Morning</SelectItem><SelectItem value="evening">Evening</SelectItem></SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("col.no")}</TableHead><TableHead>{t("farmers.farmer")}</TableHead><TableHead>{t("col.shift")}</TableHead>
                    <TableHead className="text-right">{t("common.qty")}</TableHead><TableHead className="text-right">FAT</TableHead>
                    <TableHead className="text-right">{t("common.amount")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading
                    ? Array.from({ length: 6 }).map((_, i) => <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-8" /></TableCell></TableRow>)
                    : (data?.items || []).map((c) => (
                        <TableRow key={c.id} className="hover:bg-muted/50" data-testid={`collection-row-${c.collection_number}`}>
                          <TableCell className="tnum text-xs">{c.collection_number}</TableCell>
                          <TableCell><p className="font-medium">{c.farmer_name}</p><p className="text-xs text-muted-foreground">{c.date} · {c.milk_type}</p></TableCell>
                          <TableCell>{c.shift === "morning" ? <Sun className="h-4 w-4 text-warning" /> : <Moon className="h-4 w-4 text-secondary-foreground" />}</TableCell>
                          <TableCell className="text-right tnum">{c.quantity}</TableCell>
                          <TableCell className="text-right tnum">{c.fat}</TableCell>
                          <TableCell className="text-right font-semibold tnum">{money(c.amount)}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => setReceipt(c)} data-testid={`reprint-${c.collection_number}`}><Printer className="h-4 w-4" /></Button>
                            {canDelete && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader><AlertDialogTitle>Delete {c.collection_number}?</AlertDialogTitle><AlertDialogDescription>This removes the collection entry.</AlertDialogDescription></AlertDialogHeader>
                                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground" onClick={() => del.mutate(c.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  {!isLoading && data?.items?.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">{t("col.none")}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
            <TablePagination page={page} pages={data?.pages} total={data?.total} onPage={setPage} />
          </CardContent>
        </Card>
      </div>

      {/* Thermal receipt */}
      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader><DialogTitle className="font-heading">{t("col.receipt")}</DialogTitle></DialogHeader>
          {receipt && (
            <div id="thermal-receipt" className="mx-auto w-[280px] rounded border bg-white p-4 font-mono text-[12px] text-black">
              <div className="text-center">
                <p className="text-[14px] font-bold">{tenant?.center_name}</p>
                <p>{tenant?.village} {tenant?.district}</p>
                <p className="my-1 border-y border-dashed border-black py-0.5">{t("receipt.milkTitle")}</p>
              </div>
              <div className="space-y-0.5">
                <Line k={t("receipt.receipt")} v={receipt.collection_number} />
                <Line k={t("receipt.date")} v={`${receipt.date} ${t(`common.${receipt.shift}`)}`} />
                <Line k={t("receipt.farmer")} v={`${receipt.farmer_code} ${receipt.farmer_name}`} />
                <Line k={t("receipt.type")} v={t(`common.${receipt.milk_type}`)} />
                <div className="my-1 border-t border-dashed border-black" />
                <Line k={t("receipt.qty")} v={receipt.quantity} />
                <Line k="FAT" v={receipt.fat} />
                <Line k="SNF" v={receipt.snf} />
                <Line k={t("receipt.rate")} v={money(receipt.rate)} />
                <div className="my-1 border-t border-dashed border-black" />
                <div className="flex justify-between text-[14px] font-bold"><span>{t("receipt.total")}</span><span>{money(receipt.amount)}</span></div>
                <div className="my-1 border-t border-dashed border-black" />
                <p className="text-center">{t("receipt.thanks")}</p>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            {canSms && receipt && <SmsButton mobile={mobileOf(receipt.farmer_id)} message={collectionSms(receipt)} variant="outline" size="default" testId="sms-collection-receipt" />}
            <Button className="flex-1" onClick={() => window.print()} data-testid="print-receipt-btn"><Printer className="mr-1.5 h-4 w-4" /> {t("common.print")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Line({ k, v }) {
  return <div className="flex justify-between"><span>{k}</span><span>{v}</span></div>;
}
